"use client";

export type MetricModelingStatus =
  | "modelable"
  | "needs_baseline"
  | "not_modelable";

export interface MetricModelabilityIssue {
  requiredConcept: string | null;
  expectedUnit: string | null;
  grain: string | null;
  reason: string;
}

export interface MetricModelabilityCardMetric {
  id: string;
  label: string;
  modelingStatus?: MetricModelingStatus;
  modelingIssue?: MetricModelabilityIssue;
}

export interface MetricModelabilityCardProps {
  metric: MetricModelabilityCardMetric;
}

/**
 * One selected metric that produced no benefit stream.
 *
 * Missing numeric inputs remain on normal editable fact and assumption rows;
 * this card is reserved for a metric with no valid annual-dollar formula.
 */
export function MetricModelabilityCard({
  metric,
}: MetricModelabilityCardProps) {
  const issue = metric.modelingIssue;
  const statusExplanation =
    metric.modelingStatus === "not_modelable"
      ? "We cannot create a credible annual-dollar calculation for this metric with the available evidence."
      : "This metric was not included in a financial model.";

  return (
    <article className="rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-canvas) p-4">
      <h3 className="m-0 text-[14px] font-semibold text-(--fg-primary)">
        {metric.label}
      </h3>
      <p className="mt-1 mb-0 text-[13px] leading-5 text-(--fg-secondary)">
        {statusExplanation}
      </p>
      {issue?.reason && (
        <p className="mt-2 mb-0 text-[13px] leading-5 text-(--fg-secondary)">
          {issue.reason}
        </p>
      )}
    </article>
  );
}
